import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Storage as StorageIcon,
  Transform as TransformIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AttendanceDataMigrationService } from '../../services/attendanceDataMigrationService';

interface MigrationResult {
  totalSubjects: number;
  migratedSubjects: number;
  errors: string[];
}

export function DataMigration() {
  const [loading, setLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>('');

  const handleMigration = async () => {
    try {
      setLoading(true);
      setCurrentStep('Starting migration...');
      
      const result = await AttendanceDataMigrationService.migrateAllSubjects();
      setMigrationResult(result);
      setCurrentStep('Migration completed');
    } catch (error) {
      console.error('Migration failed:', error);
      setMigrationResult({
        totalSubjects: 0,
        migratedSubjects: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error occurred']
      });
    } finally {
      setLoading(false);
      setConfirmDialog(false);
    }
  };

  const handleTestMigration = async () => {
    try {
      setLoading(true);
      setCurrentStep('Testing data migration on subject 213...');
      
      // Test migration on the specific subject from the image
      await AttendanceDataMigrationService.migrateLegacySubjectData('213');
      
      setCurrentStep('Test migration completed successfully');
      setTimeout(() => setCurrentStep(''), 3000);
    } catch (error) {
      console.error('Test migration failed:', error);
      setCurrentStep(`Test migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const resetResults = () => {
    setMigrationResult(null);
    setCurrentStep('');
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Data Migration Tool
      </Typography>

      <Grid container spacing={3}>
        {/* Migration Overview */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <StorageIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Legacy Data Migration</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Convert student-based attendance data to modern date-based structure
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>What this migration does:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  • Converts individual student attendance arrays to date-based attendance records<br/>
                  • Maintains all existing attendance data<br/>
                  • Adds statistical calculations (present count, absent count, attendance rates)<br/>
                  • Makes data compatible with the new attendance viewing system
                </Typography>
              </Alert>

              <Typography variant="h6" sx={{ mb: 2 }}>Current Data Format Issues:</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Legacy Format Detected" 
                    secondary="Data is stored in individual student attendance arrays instead of date-based structure"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Attendance Viewing Issues" 
                    secondary="Subject attendance pages may not display data correctly with the current format"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Migration Actions */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TransformIcon color="primary" />
                Migration Actions
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleTestMigration}
                  disabled={loading}
                  startIcon={<PlayArrowIcon />}
                  fullWidth
                >
                  Test Migration (Subject 213)
                </Button>

                <Button
                  variant="contained"
                  onClick={() => setConfirmDialog(true)}
                  disabled={loading}
                  startIcon={<TransformIcon />}
                  fullWidth
                  color="primary"
                >
                  Migrate All Subjects
                </Button>

                <Button
                  variant="text"
                  onClick={resetResults}
                  disabled={loading}
                  startIcon={<RefreshIcon />}
                  fullWidth
                >
                  Reset Results
                </Button>
              </Box>

              {loading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    {currentStep}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Migration Results */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Migration Results
              </Typography>

              {!migrationResult ? (
                <Alert severity="info">
                  No migration has been performed yet. Use the actions on the left to start.
                </Alert>
              ) : (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {migrationResult.totalSubjects}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Total Subjects
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          {migrationResult.migratedSubjects}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Migrated
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {migrationResult.migratedSubjects > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon />
                        <Typography variant="body2">
                          Successfully migrated {migrationResult.migratedSubjects} subjects
                        </Typography>
                      </Box>
                    </Alert>
                  )}

                  {migrationResult.errors.length > 0 && (
                    <Alert severity="error">
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Errors encountered:</strong>
                      </Typography>
                      <List dense>
                        {migrationResult.errors.map((error, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <ErrorIcon color="error" />
                            </ListItemIcon>
                            <ListItemText primary={error} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Migration Instructions
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip label="Step 1" color="primary" sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Test Migration
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Run a test migration on Subject 213 to verify the process works correctly
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip label="Step 2" color="primary" sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Full Migration
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Migrate all subjects to the new data format
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip label="Step 3" color="primary" sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Verify Results
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Check the subject attendance pages to ensure data displays correctly
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Migration</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This will migrate ALL subjects in your database to the new format. 
              This operation cannot be undone.
            </Typography>
          </Alert>
          <Typography variant="body2">
            Are you sure you want to proceed with the migration?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleMigration} variant="contained" color="primary">
            Migrate All Subjects
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 